import { Component, DestroyRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { catchError, firstValueFrom, of } from 'rxjs';
import { CompanyService } from '../../services/company.service';
import { TermsAndConditionsDialogComponent } from '../../components/terms-and-conditions-dialog/terms-and-conditions-dialog.component';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-terms-and-conditions',
  templateUrl: './terms-and-conditions.component.html',
  styleUrl: './terms-and-conditions.component.scss'
})
export class TermsAndConditionsComponent {
  private readonly router = inject(Router);
  private readonly companyService = inject(CompanyService);
  private readonly dialog = inject(MatDialog);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  public checkboxChecked = false;
  public loaded = false;
  public useConditionsSelfadjust$ = this.companyService.useConditionsSelfadjust$;
  public company$ = this.companyService.getCurrentCompanyObservable();
  public termsNotLoadedError = false;

  constructor() {
    this.company$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((company) => (this.loaded = Boolean(company)));
  }

  public toggleCheckbox(): void {
    this.checkboxChecked = !this.checkboxChecked;
  }

  public acceptTermsAndConditions(): void {
    if (!this.checkboxChecked) {
      return;
    }

    localStorage.setItem('termsAndConditionsAccepted', 'true');
    this.router.navigate(['/home']);
  }

  public async showUseConditions(): Promise<void> {
    let htmlContent = await firstValueFrom(this.useConditionsSelfadjust$);

    if (!htmlContent) {
      const company = this.companyService.getCurrentCompany();

      if (!company) {
        this.termsNotLoadedError = true;
        return;
      }

      const lang = (company.fixedLanguage || company.language || navigator.language || 'en').substring(0, 2);
      const supportedLangs = ['en', 'es', 'fr', 'de'];
      const finalLang = supportedLangs.includes(lang) ? lang : 'en';

      htmlContent = await firstValueFrom(
        this.http.get(`/assets/default-terms/${finalLang}.html`, { responseType: 'text' }).pipe(
          catchError(() => this.http.get(`/assets/default-terms/en.html`, { responseType: 'text' })),
          catchError(() => {
            this.termsNotLoadedError = true;
            return of('');
          })
        )
      );
    }

    if (!htmlContent) {
      this.termsNotLoadedError = true;
      return;
    }

    const sanitizedHtml = this.sanitizer.bypassSecurityTrustHtml(htmlContent);

    this.dialog.open(TermsAndConditionsDialogComponent, {
      data: { htmlContent: sanitizedHtml },
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      panelClass: 'full-screen-dialog'
    });
  }
}
